import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar, { type FilterGroup } from "@/components/feature/FilterBar";
import MovieCard from "@/components/feature/MovieCard";
import { movies, filterGenres, filterRegions, filterYears } from "@/mocks/movies";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);

  const genre = params.get("genre") || "全部";
  const region = params.get("region") || "全部";
  const year = params.get("year") || "全部";
  const sort = params.get("sort") || "update";

  const groups: FilterGroup[] = [
    { key: "genre", label: "影视类型", options: filterGenres },
    { key: "region", label: "地区", options: filterRegions },
    { key: "year", label: "年份", options: filterYears },
  ];
  const values = { genre, region, year };

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === "全部") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const resetFilters = () => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    setParams(next, { replace: true });
  };

  const results = useMemo(() => {
    const kw = q.trim().toLowerCase();
    const list = movies.filter((m) => {
      const matchKw =
        !kw ||
        m.title.toLowerCase().includes(kw) ||
        m.genre.toLowerCase().includes(kw) ||
        m.region.toLowerCase().includes(kw) ||
        m.actors.some((a) => a.toLowerCase().includes(kw)) ||
        m.director.toLowerCase().includes(kw) ||
        m.tags.some((t) => t.toLowerCase().includes(kw));
      const matchGenre = genre === "全部" || m.genre === genre;
      const matchRegion = region === "全部" || m.region === region;
      const matchYear = year === "全部" || String(m.year) === year;
      return matchKw && matchGenre && matchRegion && matchYear;
    });
    const sorted = [...list];
    if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    else sorted.sort((a, b) => b.year - a.year);
    return sorted;
  }, [q, genre, region, year, sort]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (input.trim()) next.set("q", input.trim());
    else next.delete("q");
    setParams(next, { replace: true });
  };

  const hot = movies.filter((m) => m.rating >= 8.6).slice(0, 5);

  return (
    <div className="w-full">
      <div className="bg-primary-950">
        <div className="w-full px-4 md:px-8 pt-6 pb-10">
          <Breadcrumb items={[{ label: "搜索" }]} />
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">搜索影视内容</h1>
          <p className="mt-3 text-sm md:text-base text-background-200/90">
            输入剧名、演员、导演或类型关键词，快速定位你想看的作品
          </p>

          <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-background-400">
                <i className="ri-search-line text-lg"></i>
              </span>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="搜索影视名称、演员、类型…"
                className="w-full pl-11 pr-4 py-3 rounded-md bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 border border-background-300 focus:border-accent-400 focus:ring-2 focus:ring-accent-200 outline-none"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap px-6 py-3 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <FilterBar groups={groups} values={values} onChange={setParam} onReset={resetFilters} />

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-sm text-foreground-600">
            {q ? (
              <>
                关键词「<strong className="text-accent-600">{q}</strong>」共找到{" "}
                <strong className="text-accent-600">{results.length}</strong> 条结果
              </>
            ) : (
              <>
                全部作品共 <strong className="text-accent-600">{results.length}</strong> 条，输入关键词可进一步筛选
              </>
            )}
          </p>
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 w-fit">
            <button
              type="button"
              onClick={() => setParam("sort", "update")}
              className={`whitespace-nowrap px-3 py-1 rounded-full text-sm transition-colors cursor-pointer ${
                sort === "update" ? "bg-accent-500 text-primary-950 font-semibold" : "text-foreground-600"
              }`}
            >
              按更新时间
            </button>
            <button
              type="button"
              onClick={() => setParam("sort", "rating")}
              className={`whitespace-nowrap px-3 py-1 rounded-full text-sm transition-colors cursor-pointer ${
                sort === "rating" ? "bg-accent-500 text-primary-950 font-semibold" : "text-foreground-600"
              }`}
            >
              按热度
            </button>
          </div>
        </div>

        {results.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
            {results.map((m) => (
              <MovieCard key={m.id} item={m} />
            ))}
          </div>
        ) : (
          <div className="mt-8 rounded-lg bg-background-100 border border-background-200 p-6 md:p-10">
            <div className="flex flex-col items-center text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <p className="mt-4 font-heading text-lg font-semibold text-foreground-800">未找到相关影视内容</p>
              <p className="mt-1.5 text-sm text-foreground-500">换个关键词，或者看看下面这些热门作品</p>
            </div>

            <div className="mt-8">
              <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-fire-line text-accent-500"></i>
                热门影视推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {hot.map((m) => (
                  <Link
                    key={m.id}
                    to={`/detail/${m.id}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-accent-300 transition-colors cursor-pointer"
                  >
                    <span className="w-10 h-14 shrink-0 rounded-md overflow-hidden bg-background-200">
                      <img src={m.cover} alt={m.title} className="w-full h-full object-top" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-foreground-950 line-clamp-1">{m.title}</span>
                      <span className="block text-xs text-foreground-500 mt-0.5">
                        {m.category} · {m.genre} · {m.year}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}